import { useEffect, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Autocomplete, Box, Divider, IconButton, Slider, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography, Button,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'

import {
  selectTables, selectSides, selectVendors,
  selectSelectedTable, selectSelectedType,
  selectSelectedSides, selectSelectedVendors,
  selectPreset, selectDateFrom, selectDateTo,
  selectVolumeThreshold, selectMedianWindow,
  setSelectedTable, setSelectedType,
  setSelectedSides, setSelectedVendors,
  setPreset, setCustomRange,
  setVolumeThreshold, setMedianWindow,
} from '../../reducers/dataIntegrityReducer'
import { loadData, loadSidesAndVendors } from '../../actions/dataIntegrityActions'

const TYPE_OPTIONS = [
  { label: 'Site + Cell', value: 'both' },
  { label: 'Site',        value: 'Site' },
  { label: 'Cell',        value: 'Cell' },
]

const PRESETS = [
  { label: '7d',     value: '7d'     },
  { label: '31d',    value: '31d'    },
  { label: '90d',    value: '90d'    },
  { label: 'Custom', value: 'custom' },
]

const smallText = { fontSize: 12 }

export default function DataIntegrityFilterBar() {
  const dispatch = useDispatch()

  const tables          = useSelector(selectTables)
  const sides           = useSelector(selectSides)
  const vendors         = useSelector(selectVendors)
  const selectedTable   = useSelector(selectSelectedTable)
  const selectedType    = useSelector(selectSelectedType)
  const selectedSides   = useSelector(selectSelectedSides)
  const selectedVendors = useSelector(selectSelectedVendors)
  const preset          = useSelector(selectPreset)
  const dateFrom        = useSelector(selectDateFrom)
  const dateTo          = useSelector(selectDateTo)
  const volumeThreshold = useSelector(selectVolumeThreshold)
  const medianWindow    = useSelector(selectMedianWindow)

  const [localFrom, setLocalFrom] = useState(dateFrom)
  const [localTo,   setLocalTo]   = useState(dateTo)

  useEffect(() => {
    if (preset !== 'custom') {
      setLocalFrom(dateFrom)
      setLocalTo(dateTo)
    }
  }, [dateFrom, dateTo, preset])

  const triggerLoad = useCallback((overrides = {}) => {
    dispatch(loadData({
      tableFilter:  selectedTable,
      type:         selectedType === 'both' ? null : selectedType,
      sides:        selectedSides.length   ? selectedSides   : null,
      vendors:      selectedVendors.length ? selectedVendors : null,
      dateFrom,
      dateTo,
      medianWindow,
      ...overrides,
    }))
  }, [dispatch, selectedTable, selectedType, selectedSides, selectedVendors, dateFrom, dateTo, medianWindow])

  const handleTableChange = (_, value) => {
    if (!value) return
    dispatch(setSelectedTable(value))
    dispatch(setSelectedSides([]))
    dispatch(setSelectedVendors([]))
    dispatch(loadSidesAndVendors({ tableFilter: value, sides: [] }))
    triggerLoad({ tableFilter: value, sides: null, vendors: null })
  }

  const handleTypeChange = (_, value) => {
    if (!value) return
    dispatch(setSelectedType(value))
    triggerLoad({ type: value === 'both' ? null : value })
  }

  const handleSideChange = (_, value) => {
    dispatch(setSelectedSides(value))
    dispatch(setSelectedVendors([]))
    dispatch(loadSidesAndVendors({ tableFilter: selectedTable, sides: value }))
    triggerLoad({ sides: value.length ? value : null, vendors: null })
  }

  const handleVendorChange = (_, value) => {
    dispatch(setSelectedVendors(value))
    triggerLoad({ vendors: value.length ? value : null })
  }

  const handlePreset = (_, value) => {
    if (!value) return
    if (value !== 'custom') {
      dispatch(setPreset(value))
      const t = new Date()
      const fmt = d => d.toISOString().slice(0, 10)
      const daysBack = value === '7d' ? 6 : value === '31d' ? 30 : 89
      const from = new Date(t); from.setDate(t.getDate() - daysBack)
      triggerLoad({ dateFrom: fmt(from), dateTo: fmt(t) })
    } else {
      dispatch(setPreset('custom'))
    }
  }

  const handleCustomApply = () => {
    if (!localFrom || !localTo || localFrom > localTo) return
    dispatch(setCustomRange({ dateFrom: localFrom, dateTo: localTo }))
    triggerLoad({ dateFrom: localFrom, dateTo: localTo })
  }

  const acProps = {
    slotProps: { input: { style: smallText }, listbox: { sx: { fontSize: 12 } } },
  }

  return (
    <Box sx={{
      px: 2, py: 0.75,
      bgcolor: 'background.paper',
      borderBottom: '1px solid',
      borderColor: 'divider',
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 1.5,
    }}>

      {/* Table */}
      <Autocomplete
        size="small" options={tables}
        value={selectedTable || null}
        onChange={handleTableChange}
        disableClearable sx={{ minWidth: 190 }} {...acProps}
        renderInput={p => <TextField {...p} label="Table"
          InputProps={{ ...p.InputProps, style: smallText }}
          InputLabelProps={{ style: smallText }} />}
      />

      {/* Type */}
      <Autocomplete
        size="small" options={TYPE_OPTIONS}
        value={TYPE_OPTIONS.find(o => o.value === selectedType) ?? null}
        onChange={(e, opt) => handleTypeChange(e, opt?.value)}
        disableClearable getOptionLabel={o => o.label}
        isOptionEqualToValue={(o, v) => o.value === v.value}
        sx={{ minWidth: 130 }} {...acProps}
        renderInput={p => <TextField {...p} label="Type"
          InputProps={{ ...p.InputProps, style: smallText }}
          InputLabelProps={{ style: smallText }} />}
      />

      {/* Side */}
      <Autocomplete
        multiple size="small" options={sides}
        value={selectedSides} onChange={handleSideChange}
        disableCloseOnSelect limitTags={2}
        sx={{ minWidth: 160 }} {...acProps}
        renderInput={p => <TextField {...p} label="Side"
          placeholder={selectedSides.length === 0 ? 'All' : ''}
          InputProps={{ ...p.InputProps, style: smallText }}
          InputLabelProps={{ style: smallText }} />}
      />

      {/* Vendor */}
      <Autocomplete
        multiple size="small" options={vendors}
        value={selectedVendors} onChange={handleVendorChange}
        disableCloseOnSelect limitTags={2}
        sx={{ minWidth: 160 }} {...acProps}
        renderInput={p => <TextField {...p} label="Vendor"
          placeholder={selectedVendors.length === 0 ? 'All' : ''}
          InputProps={{ ...p.InputProps, style: smallText }}
          InputLabelProps={{ style: smallText }} />}
      />

      <Divider orientation="vertical" flexItem />

      {/* Period presets */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, lineHeight: 1 }}>Period</Typography>
        <ToggleButtonGroup
          exclusive size="small" value={preset}
          onChange={handlePreset} sx={{ height: 30 }}
        >
          {PRESETS.map(p => (
            <ToggleButton key={p.value} value={p.value}
              sx={{ px: 1.25, fontSize: 11, textTransform: 'none', py: 0 }}>
              {p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Custom date range */}
      {preset === 'custom' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField size="small" type="date" label="From"
            value={localFrom} onChange={e => setLocalFrom(e.target.value)}
            InputLabelProps={{ shrink: true, style: smallText }}
            InputProps={{ style: smallText }} sx={{ width: 135 }} />
          <Typography variant="caption" color="text.secondary">→</Typography>
          <TextField size="small" type="date" label="To"
            value={localTo} onChange={e => setLocalTo(e.target.value)}
            InputLabelProps={{ shrink: true, style: smallText }}
            InputProps={{ style: smallText }} sx={{ width: 135 }} />
          <Button size="small" variant="outlined" onClick={handleCustomApply}
            sx={{ textTransform: 'none', height: 30, fontSize: 11 }}>
            Apply
          </Button>
        </Box>
      )}

      <Divider orientation="vertical" flexItem />

      {/* Volume threshold */}
      <Tooltip title="Days with volume below this % of the median are flagged in yellow" arrow placement="top">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 150 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, lineHeight: 1 }}>
            Volume threshold: <strong>{volumeThreshold}%</strong>
          </Typography>
          <Slider
            size="small"
            value={volumeThreshold}
            min={50} max={100} step={5}
            onChange={(_, v) => dispatch(setVolumeThreshold(v))}
            sx={{ py: 0.75, color: 'primary.main' }}
          />
        </Box>
      </Tooltip>

      {/* Median window */}
      <Tooltip title="Number of days used to calculate the reference median" arrow placement="top">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, lineHeight: 1 }}>
            Median window (days)
          </Typography>
          <TextField
            size="small"
            type="number"
            value={medianWindow}
            onChange={e => {
              const v = Math.max(7, Math.min(365, parseInt(e.target.value) || 30))
              dispatch(setMedianWindow(v))
              triggerLoad({ medianWindow: v })
            }}
            inputProps={{ min: 7, max: 365, style: { ...smallText, textAlign: 'center' } }}
            sx={{ width: 80 }}
          />
        </Box>
      </Tooltip>


      {/* Refresh */}
      <Tooltip title="Refresh data" arrow placement="top">
        <IconButton size="small" onClick={() => triggerLoad()} sx={{ ml: 'auto' }}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Tooltip>

    </Box>
  )
}